<script setup lang="ts">
import MIcon from "../../MIcon";
import MSeparator from "../../MSeparator";
import { ComponentPublicInstance, Ref, computed, inject, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import MLayoutStack from "../../MLayoutStack";
import { GroupItem, Icon } from "./types";
import MExpander from "../../MExpander";
import { randomID } from "../../../../utils/helpers";
import { usePermission } from "../../../../composables/permission";
import SidebarGroupItem from "./SidebarGroupItem";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  to: [String, Object],
  icon: {
    type: Object as () => Icon,
    default: undefined,
  },
  items: {
    type: Array as () => GroupItem[],
    required: true,
  },
  selected: Boolean,
  id: String,
  maxShowItems: {
    type: Number,
    default: undefined,
  },
  moreText: {
    type: String,
    default: "Show {0} more items",
  },
  display: {
    type: Boolean,
    default: true,
  },
  small: Boolean,
  collapsable: Boolean,
  notification: Number,
});

const renderedComponent = inject<
  Ref<ComponentPublicInstance & { subnavItem?: string }> | undefined
>("renderedComponent")!;
const expanded = ref<boolean>();

const notificationCount = computed(() => {
  return (
    props.notification || props.items.map((x) => x.notification ?? 0).reduce((a, b) => a + b, 0)
  );
});

const route = useRoute();

watch(
  [renderedComponent, () => route.fullPath.replace(`${route.params.tenant}/`, "")] as const,
  ([comp, path]) => {
    if (expanded.value === undefined) {
      expanded.value = !!props.items.find(
        (x) =>
          x.selected ||
          (x.to && ~path.indexOf(x.to)) ||
          (comp?.subnavItem && x.id && comp.subnavItem === x.id),
      );
    }
  },
  { immediate: true },
);

const { hasLink, disabled, onClick } = usePermission(props, false, useRouter());

const ariaId = randomID("sidebar-group-");

// ENG-21940 a title only earns a hover/focus affordance if it links or toggles;
// plain section headings (e.g. a team name) are inert and must not look clickable.
const isCollapseToggle = computed(() => !!(props.collapsable && props.items.length));
const interactive = computed(() => !!props.to || isCollapseToggle.value);

function handleClick(ev: MouseEvent) {
  if (!interactive.value) return;

  expanded.value = !expanded.value;

  if (!hasLink.value) return;

  return onClick(ev);
}

defineExpose({
  expanded,
});
</script>
<template>
  <div
    v-show="display"
    class="group"
    :class="{
      'group-with-title': !!title,
      'group-small': small,
      'group-collapsable': items.length && collapsable,
    }"
    :role="collapsable && items.length ? 'button' : undefined"
  >
    <component
      :is="to ? 'router-link' : interactive ? 'button' : 'div'"
      v-if="title"
      class="group-title"
      :class="{
        'group-item-title': items.length === 0,
        'group-title-interactive': interactive,
        'item-collapsable': isCollapseToggle,
        'router-link-active':
          selected || (id && renderedComponent && id === renderedComponent.subnavItem),
      }"
      :to="to"
      :aria-expanded="isCollapseToggle ? expanded : undefined"
      :aria-controls="isCollapseToggle ? ariaId : undefined"
      :tabindex="interactive ? 0 : undefined"
      :disable="disabled"
      @click="handleClick"
    >
      <m-layout-stack horizontal class="items-center" gap="2">
        <m-icon
          v-if="collapsable && items.length"
          class="m-select__dropdown-icon"
          name="fa-solid fa-chevron-right"
          :class="{ 'rotate-90': expanded }"
        ></m-icon>
        <span>{{ title }}</span>
      </m-layout-stack>

      <div
        v-if="notificationCount && !expanded && (collapsable || items.length === 0)"
        class="group-item-info-container"
      >
        <div v-if="icon" class="group-item-icon">
          <m-icon :name="icon.name" />
          <span class="sr-only">{{ icon.description }}</span>
        </div>
        <div class="group-item-notification" data-testid="parent-group-item-notification">
          {{ notificationCount > 99 ? "99+" : notificationCount }}
        </div>
      </div>
    </component>
    <m-expander
      v-if="collapsable && items.length"
      :id="ariaId"
      v-model:expanded="expanded"
      auto
      transition-name="expand-medicus-fast"
      borderless
      dense
    >
      <template #head>
        <span />
      </template>
      <div>
        <template v-for="(item, i) in items" :key="i">
          <SidebarGroupItem
            v-bind="item"
            :selected="
              (item.selected ||
                (item.id &&
                  renderedComponent &&
                  item.id === renderedComponent.subnavItem)) as boolean
            "
          />

          <m-separator v-if="item.separator" :key="`separator` + i" />
        </template>
      </div>
    </m-expander>
    <template v-else>
      <template v-for="(item, i) in items" :key="i">
        <SidebarGroupItem
          v-bind="item"
          :selected="
            (item.selected ||
              (item.id && renderedComponent && item.id === renderedComponent.subnavItem)) as boolean
          "
        />
        <m-separator v-if="item.separator" :key="`separator` + i" />
      </template>
    </template>
  </div>
</template>

<style lang="scss">
$hoverColour: var(--surface-inverse-hover);
$activeColour: var(--theme-grey);
$bgItem: var(--theme-darker-blue);

.group {
  // -webkit-link: #fff;

  // padding-top: 8px;

  position: relative;
  // border-bottom: 1px solid rgba(132, 141, 156, 0.2);

  &.group-with-title .m-sidebar-group-item {
    padding-top: var(--space-4);

    padding-bottom: var(--space-3);
    padding-left: var(--space-3);
  }

  &.group-with-title::after {
    content: " ";
    position: absolute;
    width: 85%;
    left: var(--space-5);
    // border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  // ENG-21940 only titles that link or toggle get the hover/focus affordance.
  &.group-with-title > .group-title-interactive {
    &:hover,
    &:focus-visible {
      background: $hoverColour;
      outline: none;
      text-decoration: underline;
    }
  }

  &:not(.group-small) {
    .router-link-active {
      .group-item-title {
        // font-weight: bold;
      }
      background: var(--theme-grey);
    }
  }

  .item-collapsable {
    cursor: pointer;
    // matches .group-title's -10px/-15px bleed margin; a <button> won't auto-fill it like a <div> would.
    width: calc(100% + 25px);

    &:hover,
    &:focus-visible {
      background: $hoverColour;
      outline: none;

      .group-item-notification {
        background: var(--theme-grey);
      }
    }
  }

  a {
    text-decoration: none;

    color: var(--grey-lightest) !important;

    &:hover {
      background: $hoverColour;
    }
  }

  .m-expander > * {
    padding: 0;
  }
}

.group-collapsable {
  .group-item-title {
    padding-left: var(--space-7) !important;
  }
}

// ENG-21972 m-prefixed: AG Grid's theme CSS emits a bare `.group-item` that clobbered the
// negative left margin below, pushing item text 15px right of the section title.
.m-sidebar-group-item {
  display: flex;

  padding: 0.9em 0;

  align-items: center;

  justify-content: space-between;
  text-decoration: none;

  margin: 0 -10px 0 -15px;
  padding-left: var(--space-10);
  padding-right: var(--space-4);

  // ENG-21740 component-local: a cool grey rule at 20%; two sites, below the shared bar.
  --m-sidebar-group-item-border: rgb(132 141 156 / var(--opacity-20));

  border-top: 1px solid var(--m-sidebar-group-item-border);
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &.router-link-active {
    background: $activeColour;

    .group-item-notification {
      background: $bgItem;
    }
  }

  &:hover,
  &:focus-visible {
    background: $hoverColour;
    outline: none;

    .group-item-title {
      color: var(--grey-lightest);
      text-decoration: underline;
    }

    .group-item-notification {
      background: var(--brand-grey);
    }
  }
}

.group-item-title {
  font-style: normal;
  font-weight: normal;
  font-size: var(--font-size-md);
  line-height: 150%;
  align-items: center;

  display: flex;

  /* White */

  color: var(--grey-lightest);

  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;

  &.router-link-active {
    .group-item-notification {
      background: $bgItem;
    }
  }

  &:hover {
    .group-item-notification {
      background: var(--brand-grey);
    }
  }
}

.group-title {
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: bold;
  font-size: var(--font-size-md);
  line-height: 1.2;

  margin: 0 -10px 0 -15px;
  // ENG-21707 -3px under the section title
  padding: var(--space-2-5) var(--space-4) var(--space-1-5) var(--space-6);
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  // align-items: center;
  // gap: 0.5rem;
  justify-content: space-between;
}

.group-small {
  // ENG-21707 4px after the last item
  padding-bottom: var(--space-1);

  .m-sidebar-group-item {
    // height: 26px;
    border-top: none;
    padding-top: 0.43em !important;
    padding-bottom: 0.43em !important;
  }

  .group-item-title {
    font-style: normal;
    font-weight: normal;
    font-size: var(--font-size-md);
    line-height: 150%;

    // ENG-21707 align submenu item text flush with the group title (25px from edge)
    padding-left: var(--space-3);
  }
}

.group-item-info-container {
  display: flex;
  align-items: center;
  gap: var(--space-2-5);
  margin-right: var(--space-2);
  margin-left: 0.3em;

  &:empty {
    display: none;
  }
}

.group-item-icon {
  display: flex;
  align-items: center;
  font-size: var(--font-size-2xl);
}

.group-item-notification {
  padding: 0px var(--space-2);

  background: var(--theme-grey);
  border-radius: var(--radius-xl);

  font-style: normal;
  font-weight: normal;
  font-size: var(--font-size-md);
  line-height: 150%;
  /* identical to box height, or 133% */

  display: flex;
  align-items: center;
  text-align: right;

  color: var(--grey-light);

  /* Inside Auto Layout */

  flex: none;
  order: 0;
  align-self: center;
}

.show-more {
  cursor: pointer;
  // margin-top: 8px;

  font-style: normal;
  font-weight: bold;
  font-size: var(--font-size-md);
  line-height: 150%;
  /* identical to box height */

  text-underline-offset: 3px;
  text-decoration-line: underline;

  /* White */

  color: var(--grey-lightest);

  display: flex;
  // justify-content: center;

  span {
    padding-left: var(--space-8);
  }
}
</style>
